import { DateString } from "../../types"
import { parseDate } from "../../utils/date"
import { isDate, isFunction } from "../../utils/utils"
import { Popup } from "../Popup"
import { classes } from "../react"
import { DatePickerPopup } from "./DatePickerPopup"

/**
 * Props for the <DatePicker> component
 * Note: defined based on PropsWithChildren, but children are ignored
 * Leaving this as is because mupltiple call sites use the <DatePicker></DatePicker> syntax
 * rather than simply <DatePicker />
 */
type Props = React.PropsWithChildren<{
    /** Currently selected date */
    date?: Date

    /**
     * This is called when the date has changed
     *
     * If the function returns "true", and if the calendar popup (see <DatePickerPopup>)
     * is currently open, then this popup will close
     *
     * The "changeType" parameter can be used by the consumer to know how
     * the date was change, and decide to either close or leave the popup open
     *
     * For instance, when browsing through years/months, this actually changes
     * the current date (there is no distinct state for that),
     * but we generally do not want the popup to close in such cases
     *
     * Note that this function is optional.
     * If it is, the component works in "read only" mode (the input is disabled),
     * and the calendar popup cannot be open
     *
     */
    setDate?: (value: Date, changeType?: DateChangeType) => boolean | void

    /**
     * Default date, used when "date" is not set
     * Note: not quite sure when/how/if this is used in retrospect
     * We should probably review this at some point
     **/
    defaultDate?: Date

    /**
     * Label displayed next to the input
     */
    label?: React.ReactNode

    /*
     * Placeholder displayed in the input if no date set
     */
    placeholder?: string

    /*
     * If true (or if "setDate" is not set), works in read-only mode
     */
    disabled?: boolean

    /**
     * Additional CSS class names set on the outer wrapper
     */
    className?: string

    /*
     * Required input
     */
    required?: boolean

    /*
     * rangeClosingDate is used if the date picker is used to indicate an interval between
     * date and rangeClosingDate
     */
    rangeClosingDate?: Date

    /**
     * If truthy, the "invalid" class will be added to the top level element
     */
    invalid?: boolean | string

}>

/**
 * This enum defines the various events which can lead to a date to be changed
 * (i.e., "setDate" to be called)
 */
export type DateChangeType = "input" | "click" | "year" | "month"

/**
 * Implements the input part of the DatePicker component
 * See "DatePickerPopup.tsx" for the popup in charge of displaying the calendar
 *
 * This component is mostly stateless - the selected date must be handled by consumers,
 * through the "date" and "setDate" props
 */
export function DatePicker({
    date,
    setDate,
    label,
    placeholder,
    disabled,
    required,
    invalid,
    defaultDate,
    className,
    rangeClosingDate,
}: Props) {
    const [state, setState] = React.useState(() => {
        return {
            date,
            value: isDate(date) ? date.toLocaleDateString() : "",
        }
    })

    React.useEffect(() => {
        if (state.date != date) {
            try {
                setState({
                    date,
                    value: date ? date.toLocaleDateString() : "",
                })
            } catch (error) {
                console.log(error)
            }
        }
    }, [state.date, date])

    disabled = disabled || !isFunction(setDate)

    const update = (v: string) => {
        const date = !v ? null : parseDate(v)

        setState({
            date,
            value: v,
        })

        if (isFunction(setDate)) {
            setDate(date, "input")
        }
    }

    const getPopupProps = () => ({
        date,
        defaultDate,
        setDate,
        rangeClosingDate,
    })

    const input = (
        <div className="input-group">
            <input
                type="text"
                className="form-control"
                placeholder={placeholder}
                value={state.value}
                disabled={disabled}
                required={required}
                onChange={(e) => update(e.target.value)}
            ></input>
            <span className="input-group-btn">
                <Popup
                    popupContent={DatePickerPopup}
                    popupContentProps={getPopupProps}
                    wrapper={false}
                >
                    <button
                        className="btn btn-default"
                        type="button"
                        disabled={disabled}
                    >
                        <i className="icon icon-calendar"></i>
                    </button>
                </Popup>
            </span>
        </div>
    )

    return (
        <div
            className={classes("omnia-date-picker", className, invalid && "invalid")}
            onClick={(e) => e.stopPropagation()}
        >
            {label ? (
                <div className="form-group">
                    <label>{label}</label>
                    {input}
                </div>
            ) : (
                input
            )}
        </div>
    )
}

interface DateStringPickerProps
    extends Omit<Props, "date" | "setDate" | "rangeClosingDate"> {
    date: DateString
    setDate?: (
        value: DateString,
        changeType?: DateChangeType
    ) => boolean | void
    rangeClosingDate?: DateString
}

/**
 * Alternate version of "DatePicker" for call sites using strings to describe dates
 *
 * In Redux based projects, we will generally store dates as strings so that they can be stored in the global store,
 * where non-serialized values (such instances of the Date class) are frowned upon
 *
 * This is simply a wrapper around <DatePicker>, tasked with converting the date from/to string/Date
 *
 */
export function DateStringPicker(props: DateStringPickerProps) {
    const date = React.useMemo(
        () => (props.date ? new Date(props.date) : undefined),
        [props.date]
    )

    const rangeClosingDate = React.useMemo(
        () =>
            props.rangeClosingDate
                ? new Date(props.rangeClosingDate)
                : undefined,
        [props.rangeClosingDate]
    )

    const setDate = (value: Date, changeType?: DateChangeType) => {
        const date = value ? value.toISOString() : undefined
        props.setDate(date, changeType)

        // If changeType is set to "click", the date picker will close after the date is set
        // Note: this is not  related to the string/Date conversion - rather a shortcut
        // By default the calendar does not close when a date is picked, and we got a request
        // for it to close automatically when doing so in the Protection module,
        // which is currently the only user for this <DateStringPicker> component
        // At some point, the logic should be moved to <DateString>, enabled by a new dedicated prop
        if (changeType === "click") {
            return true
        }
    }

    const readOnly = !props.setDate

    return (
        <DatePicker
            {...{
                ...props,
                date,
                setDate: readOnly ? undefined : setDate,
                rangeClosingDate: rangeClosingDate,
            }}
        />
    )
}
